// Engineer PWA "Add my jobs to my calendar" screen (Session ATY). The engineer makes a private calendar link
// (an .ics feed of their own jobs and standby weeks, server/src/modules/calendarSync/engineerFeed.ts) and subscribes
// to it in Google, Apple or Outlook.
//
// THE LINK IS SHOWN ONCE. The server keeps only a hash of the link's token, so the POST that makes a link is the only
// reply that ever carries it. It is held in this component's state and nowhere else: never localStorage,
// sessionStorage or IndexedDB, never logged, never put in a URL. A later visit can only say the link is on, make a new
// one (which stops the old one at once) or turn it off.
//
// GATED ON hasOwnEngineerRecords, like Next of kin: the feed hangs off an Engineer row, which the server resolves by
// fact (ownEngineerIdForUser, so the Director's linked admin login works as the engineer it is linked to). A login with
// no engineer row is told so, both by the client gate and by the server's 404 no_engineer_record.
//
// Online only: making, replacing and turning off a link are never queued offline, because the link exists only in the
// reply and a queued request would have nowhere to show it.
import { useEffect, useState } from "react";
import { useAuth } from "../../lib/auth";
import { hasOwnEngineerRecords } from "../../lib/pwaIdentity";
import { OversightNotice } from "./OversightNotice";
import { ApiError, api } from "../../lib/api";
import type { CalendarFeedLinks, CalendarFeedSummary } from "../../lib/api";
import { ConfirmButton } from "../../components/ConfirmButton";
import { copyText } from "../../lib/clipboard";
import { friendlyApiError } from "../../lib/friendlyApiError";

const TITLE = "Add my jobs to my calendar";

const PRIMARY_BTN =
  "min-h-11 rounded-ele bg-ele-green px-4 font-menu text-sm font-semibold tracking-wide text-ele-bg transition disabled:opacity-40";
const SECONDARY_BTN =
  "inline-flex min-h-11 items-center justify-center rounded-ele border border-ele-card-border px-4 font-menu text-sm tracking-wide text-ele-strong transition hover:border-ele-green disabled:opacity-40";

// A London calendar day, the platform's time-zone rendering rule.
function londonDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { timeZone: "Europe/London", day: "numeric", month: "short", year: "numeric" });
}

// When the calendar app last read the feed, in words: minutes or hours ago while recent, otherwise the London day.
export function lastReadInWords(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const mins = Math.max(0, Math.round((now - t) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  return `on ${londonDay(iso)}`;
}

export function CalendarFeed() {
  const { user } = useAuth();
  const ownRecords = hasOwnEngineerRecords(user);

  const [feed, setFeed] = useState<CalendarFeedSummary | null>(null);
  // The link, ONLY in memory and only after this screen made it. Never written anywhere else.
  const [links, setLinks] = useState<CalendarFeedLinks | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error" | "no_record">("loading");
  const [busy, setBusy] = useState<"create" | "off" | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!ownRecords) {
      setLoadState("ready");
      return;
    }
    let live = true;
    (async () => {
      try {
        const res = await api.engineers.myCalendarFeed();
        if (!live) return;
        setFeed(res.feed);
        setLoadState("ready");
      } catch (err) {
        if (!live) return;
        setLoadState(err instanceof ApiError && err.status === 404 && err.code === "no_engineer_record" ? "no_record" : "error");
      }
    })();
    return () => {
      live = false;
    };
  }, [ownRecords, reloadKey]);

  async function makeLink() {
    setBusy("create");
    setActionError(null);
    setNotice(null);
    setCopyState("idle");
    try {
      const res = await api.engineers.createMyCalendarFeed();
      setFeed(res.feed);
      setLinks(res.links);
      if (res.replaced) setNotice("Your new link is ready. The old link has stopped working.");
    } catch (err) {
      if (err instanceof ApiError && err.status === 404 && err.code === "no_engineer_record") {
        setLoadState("no_record");
      } else {
        // A 409 feed_changed carries the server's own sentence; read the feed again so the screen shows what is true.
        setActionError(friendlyApiError(err, "Could not make your calendar link. Please try again."));
        if (err instanceof ApiError && err.status === 409) setReloadKey((k) => k + 1);
      }
    } finally {
      setBusy(null);
    }
  }

  async function turnOff() {
    setBusy("off");
    setActionError(null);
    setNotice(null);
    try {
      await api.engineers.revokeMyCalendarFeed();
      setFeed(null);
      setLinks(null);
      setCopyState("idle");
      setNotice("Your calendar link is off. Your calendar app stops getting jobs at its next refresh.");
    } catch (err) {
      setActionError(friendlyApiError(err, "Could not turn off your calendar link. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  async function copyLink() {
    if (!links) return;
    const ok = await copyText(links.url);
    setCopyState(ok ? "copied" : "failed");
  }

  if (!ownRecords || loadState === "no_record") {
    return (
      <OversightNotice
        title={TITLE}
        explanation="This account has no engineer profile, so there are no jobs of its own to put in a calendar."
      />
    );
  }

  const isOn = Boolean(feed?.on);

  return (
    <div>
      <h1 className="mb-1 font-heading text-2xl tracking-wide text-ele-strong">{TITLE}</h1>
      <p className="mb-3 text-sm text-ele-body">
        Put your ELE jobs and standby weeks in your own calendar app (Google, Apple or Outlook). It shows each job&apos;s time,
        type, reference and the site&apos;s address. It never shows prices, customer contact details or access codes: those
        stay here in the app.
      </p>
      <p className="mb-4 rounded-lg border border-amber-500 bg-amber-500/10 p-3 text-sm text-[var(--ele-amber-text-soft)]">
        This link shows where your jobs are. Do not share it. Calendar apps refresh on their own schedule (Google every few
        hours), so the job list in this app is always the up-to-the-minute version.
      </p>

      {loadState === "loading" ? (
        <p className="text-sm text-ele-body">Loading...</p>
      ) : loadState === "error" ? (
        <p role="alert" className="rounded-lg border border-ele-card-border bg-ele-card p-4 text-sm text-ele-strong">
          Could not check your calendar link. Please try again in a moment.
        </p>
      ) : (
        <div className="space-y-4">
          {notice && (
            <p role="status" className="text-sm text-ele-green">
              {notice}
            </p>
          )}

          {links && (
            <section className="rounded-lg border border-ele-card-border bg-ele-card p-4" aria-label="Your new calendar link">
              <label htmlFor="calendar-feed-link" className="mb-1 block text-sm text-ele-body">
                Your calendar link
              </label>
              <input
                id="calendar-feed-link"
                readOnly
                value={links.url}
                onFocus={(e) => e.currentTarget.select()}
                className="w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none"
              />
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button type="button" onClick={() => void copyLink()} className={SECONDARY_BTN}>
                  Copy link
                </button>
                {copyState === "copied" && (
                  <span role="status" className="text-sm text-ele-green">
                    Link copied.
                  </span>
                )}
              </div>
              {copyState === "failed" && (
                <p role="alert" className="mt-2 text-sm text-ele-strong">
                  Could not copy it automatically. Press and hold the link above, then choose Copy.
                </p>
              )}

              <div className="mt-4 grid gap-2">
                <a href={links.google} target="_blank" rel="noopener noreferrer" className={SECONDARY_BTN}>
                  Add to Google Calendar
                </a>
                <a href={links.outlook} target="_blank" rel="noopener noreferrer" className={SECONDARY_BTN}>
                  Add to Outlook
                </a>
                <a
                  href={links.outlookWork}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-11 items-center justify-center font-menu text-xs tracking-wide text-ele-body underline"
                >
                  Work or school Outlook
                </a>
              </div>
              <p className="mt-3 text-sm text-ele-body">
                <span className="font-semibold text-ele-strong">Apple Calendar (iPhone, iPad or Mac):</span> copy the link above,
                then in the Calendar app choose Calendars, Add Calendar, Add Subscription Calendar, and paste it.
              </p>

              <p className="mt-4 text-sm font-semibold text-ele-strong">Save it now: for your security this link is shown only once.</p>
            </section>
          )}

          {isOn && feed ? (
            <section className="rounded-lg border border-ele-card-border bg-ele-card p-4">
              <p className="text-sm text-ele-strong">Your calendar link is on (since {londonDay(feed.createdAt)}).</p>
              {feed.lastFetchedAt ? (
                <p className="mt-1 text-sm text-ele-body">Last read by your calendar app {lastReadInWords(feed.lastFetchedAt)}.</p>
              ) : (
                <p className="mt-1 text-sm text-ele-body">Your calendar app has not read it yet.</p>
              )}
              {!links && (
                <p className="mt-2 text-xs text-ele-body">
                  The link itself cannot be shown again. If you have lost it, make a new link and add that instead.
                </p>
              )}
              <div className="mt-3 flex flex-wrap gap-3">
                <ConfirmButton
                  label="Make a new link"
                  confirmLabel="Make a new link? The old one stops working straight away."
                  onConfirm={() => void makeLink()}
                  busy={busy === "create"}
                  busyLabel="Making..."
                  disabled={busy !== null && busy !== "create"}
                  resetMs={6000}
                  className={SECONDARY_BTN}
                />
                <ConfirmButton
                  label="Turn off"
                  confirmLabel="Turn off your calendar link? Your calendar app stops getting jobs."
                  onConfirm={() => void turnOff()}
                  busy={busy === "off"}
                  busyLabel="Turning off..."
                  disabled={busy !== null && busy !== "off"}
                  resetMs={6000}
                  className={SECONDARY_BTN}
                />
              </div>
            </section>
          ) : (
            <button type="button" onClick={() => void makeLink()} disabled={busy !== null} className={PRIMARY_BTN}>
              {busy === "create" ? "Making your link..." : "Create my calendar link"}
            </button>
          )}

          {actionError && (
            <p role="alert" className="text-sm text-ele-danger">
              {actionError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
